import React, { useEffect, useState } from "react";
import { Youtube, Facebook, Twitter, Linkedin, Instagram, Save, Image as ImageIcon, Loader2, Mail, Phone, MessageCircle, GraduationCap } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import FileUploader from "@/components/FileUploader";

const FIELDS = [
  { key: "youtube_url", label: "YouTube URL", icon: Youtube, placeholder: "https://www.youtube.com/watch?v=...", testid: "i-social-youtube" },
  { key: "facebook_url", label: "Facebook URL", icon: Facebook, placeholder: "https://facebook.com/your-page", testid: "i-social-facebook" },
  { key: "twitter_url", label: "X (Twitter) URL", icon: Twitter, placeholder: "https://x.com/your-handle", testid: "i-social-twitter" },
  { key: "linkedin_url", label: "LinkedIn URL", icon: Linkedin, placeholder: "https://www.linkedin.com/company/your-page", testid: "i-social-linkedin" },
  { key: "instagram_url", label: "Instagram URL", icon: Instagram, placeholder: "https://www.instagram.com/your-handle", testid: "i-social-instagram" },
];

const CONTACT_FIELDS = [
  { key: "email", label: "Email ID", icon: Mail, type: "email", placeholder: "principal@institute.in", testid: "i-social-email" },
  { key: "contact_phone", label: "Contact Number (General Office)", icon: Phone, type: "tel", placeholder: "+91 …", testid: "i-social-contact-phone" },
  { key: "whatsapp_phone", label: "WhatsApp Contact Number", icon: MessageCircle, type: "tel", placeholder: "+91 …", testid: "i-social-whatsapp-phone" },
  { key: "admission_phone", label: "Admission Contact Number", icon: GraduationCap, type: "tel", placeholder: "+91 …", testid: "i-social-admission-phone" },
];

const EMPTY = {
  youtube_url: "",
  facebook_url: "",
  twitter_url: "",
  linkedin_url: "",
  instagram_url: "",
  home_image_url: "",
  home_image_public_id: "",
  email: "",
  contact_phone: "",
  whatsapp_phone: "",
  admission_phone: "",
};

export default function InstituteSocialMedia() {
  const { role } = useAuth();
  const canEdit = role === "institute_admin";

  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/institute/social-media");
        if (cancelled) return;
        setForm({
          youtube_url: data?.youtube_url || "",
          facebook_url: data?.facebook_url || "",
          twitter_url: data?.twitter_url || "",
          linkedin_url: data?.linkedin_url || "",
          instagram_url: data?.instagram_url || "",
          home_image_url: data?.home_image_url || "",
          home_image_public_id: data?.home_image_public_id || "",
          email: data?.email || "",
          contact_phone: data?.contact_phone || "",
          whatsapp_phone: data?.whatsapp_phone || "",
          admission_phone: data?.admission_phone || "",
        });
      } catch (err) {
        if (!cancelled) toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed to load");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!canEdit) return;
    setSaving(true);
    try {
      const { data } = await api.put("/institute/social-media", form);
      setForm((f) => ({
        ...f,
        home_image_url: data?.home_image_url || "",
        home_image_public_id: data?.home_image_public_id || "",
      }));
      toast.success("Social media links saved");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const hasYoutube = (form.youtube_url || "").trim().length > 0;

  return (
    <div className="space-y-6" data-testid="i-social-media-page">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-md bg-[#234333]/10 text-[#234333] flex items-center justify-center shrink-0">
          <Youtube size={20} strokeWidth={1.5} />
        </div>
        <div className="flex-1">
          <h1 className="font-serif text-2xl text-[#1c1a17]">Social Media & Home Image</h1>
          <p className="text-sm text-[#6b665e] mt-1">
            Add your institute&apos;s social handles. Empty fields are automatically hidden on the public website. The YouTube URL controls the homepage video embed; when blank, the uploaded Home Image is shown in its place.
          </p>
        </div>
      </div>

      <div className="bg-white rounded-md shadow-sm border border-[#e5e0d8] p-6 space-y-5">
        {loading ? (
          <div className="flex items-center gap-2 text-[#6b665e] text-sm">
            <Loader2 size={14} className="animate-spin" /> Loading…
          </div>
        ) : (
          <>
            <div className="grid md:grid-cols-2 gap-4">
              {CONTACT_FIELDS.map(({ key, label, icon: Icon, type, placeholder, testid }) => (
                <div key={key} className="space-y-1.5">
                  <label className="flex items-center gap-2 text-xs font-medium text-[#1c1a17]">
                    <Icon size={14} strokeWidth={1.8} className="text-[#234333]" />
                    {label}
                  </label>
                  <input
                    type={type}
                    data-testid={testid}
                    placeholder={placeholder}
                    value={form[key]}
                    onChange={(e) => set(key, e.target.value)}
                    disabled={!canEdit}
                    className="w-full text-sm border border-[#d8d4cb] rounded-sm px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-[#234333]/20 focus:border-[#234333] disabled:bg-slate-50"
                  />
                </div>
              ))}
            </div>

            <div className="pt-5 border-t border-[#e5e0d8]">
              <div className="text-[11px] tracking-[0.18em] uppercase text-[#6b665e] font-semibold mb-3">
                Social Media
              </div>
              <div className="grid md:grid-cols-2 gap-4">
              {FIELDS.map(({ key, label, icon: Icon, placeholder, testid }) => (
                <div key={key} className="space-y-1.5">
                  <label className="flex items-center gap-2 text-xs font-medium text-[#1c1a17]">
                    <Icon size={14} strokeWidth={1.8} className="text-[#234333]" />
                    {label}
                  </label>
                  <input
                    type="url"
                    data-testid={testid}
                    placeholder={placeholder}
                    value={form[key]}
                    onChange={(e) => set(key, e.target.value)}
                    disabled={!canEdit}
                    className="w-full text-sm border border-[#d8d4cb] rounded-sm px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-[#234333]/20 focus:border-[#234333] disabled:bg-slate-50"
                  />
                </div>
              ))}
              </div>
            </div>

            <div className="pt-5 border-t border-[#e5e0d8]">
              <div className="flex items-start gap-2 mb-2">
                <ImageIcon size={14} strokeWidth={1.8} className="text-[#234333] mt-0.5" />
                <div className="flex-1">
                  <div className="text-xs font-medium text-[#1c1a17]">
                    Home Page Image (shown when YouTube URL is blank)
                  </div>
                  <div className="text-[11px] text-[#6b665e] mt-0.5">
                    {hasYoutube
                      ? "Currently your homepage shows the YouTube embed. This image is used only as a fallback if you clear the URL."
                      : "Currently your homepage will display this image (no YouTube URL set)."}
                  </div>
                </div>
              </div>
              <FileUploader
                kind="inst_home_image"
                label="Home image"
                url={form.home_image_url}
                testid="i-social-home-image"
                onChange={(v) => {
                  setForm((f) => ({
                    ...f,
                    home_image_url: v.url || "",
                    home_image_public_id: v.public_id || "",
                  }));
                }}
              />
            </div>

            {canEdit && (
              <div className="flex justify-end pt-2">
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saving}
                  data-testid="i-social-save-btn"
                  className="inline-flex items-center gap-1.5 bg-[#234333] hover:bg-[#1a3326] disabled:opacity-60 text-white text-xs font-medium px-4 py-2.5 rounded-sm"
                >
                  {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} strokeWidth={1.8} />}
                  Save Changes
                </button>
              </div>
            )}

            {!canEdit && (
              <div className="text-[11px] text-[#a8a298]">
                Only Institute Admins can edit social media links.
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
